import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getGanttTasks, createGanttTask, updateGanttTask, deleteGanttTask, getUsers } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Loader2, Trash2, Edit, GanttChart, CalendarRange } from 'lucide-react';
import { toast } from 'sonner';

const COLORS = ['#2B4257', '#4A7C59', '#D98A6C', '#C25953', '#5C7083', '#8B6F47', '#6B8E7B', '#3D5A75'];
const CATEGORIES = ['Operaciones', 'Marketing', 'Infraestructura', 'RRHH', 'Ventas', 'Otro'];

const Gantt = () => {
  const { isDirection } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [form, setForm] = useState({
    title: '', assigned_to: '', start_date: '', end_date: '', progress: 0, color: COLORS[0], category: 'Operaciones'
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [tasksRes, usersRes] = await Promise.all([getGanttTasks(), getUsers()]);
      setTasks(tasksRes.data);
      setUsers(usersRes.data || []);
    } catch (e) { console.error('Error cargando datos Gantt:', e.message); }
    finally { setLoading(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingTask) {
        await updateGanttTask(editingTask.id, form);
        toast.success('Tarea actualizada');
      } else {
        await createGanttTask(form);
        toast.success('Tarea creada');
      }
      setShowDialog(false);
      setEditingTask(null);
      setForm({ title: '', assigned_to: '', start_date: '', end_date: '', progress: 0, color: COLORS[0], category: 'Operaciones' });
      fetchData();
    } catch (e) { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar esta tarea?')) return;
    try { await deleteGanttTask(id); toast.success('Eliminada'); fetchData(); }
    catch (e) { toast.error('Error al eliminar'); }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setForm({
      title: task.title, assigned_to: task.assigned_to || '', start_date: task.start_date,
      end_date: task.end_date, progress: task.progress || 0, color: task.color || COLORS[0],
      category: task.category || 'Operaciones'
    });
    setShowDialog(true);
  };

  const handleProgressUpdate = async (task, newProgress) => {
    try {
      await updateGanttTask(task.id, { ...task, progress: newProgress });
      fetchData();
    } catch (e) { console.error('Error cargando datos Gantt:', e.message); }
  };

  if (!isDirection()) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <GanttChart className="mx-auto h-12 w-12 text-[#E8E2D7] mb-4" />
          <h2 className="text-xl font-semibold text-[#1A2835]">Acceso Restringido</h2>
          <p className="text-[#5C7083]">Solo Dirección puede acceder</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center h-full"><Loader2 className="h-8 w-8 animate-spin text-[#2B4257]" /></div>;
  }

  // Compute Gantt chart layout
  const allDates = tasks.flatMap(t => [new Date(t.start_date), new Date(t.end_date)]);
  const minDate = allDates.length > 0 ? new Date(Math.min(...allDates)) : new Date();
  const maxDate = allDates.length > 0 ? new Date(Math.max(...allDates)) : new Date();
  const totalDays = Math.max(Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24)) + 1, 7);

  const getBarStyle = (task) => {
    const start = new Date(task.start_date);
    const end = new Date(task.end_date);
    const left = ((start - minDate) / (1000 * 60 * 60 * 24) / totalDays) * 100;
    const width = (((end - start) / (1000 * 60 * 60 * 24) + 1) / totalDays) * 100;
    return { left: `${Math.max(left, 0)}%`, width: `${Math.max(width, 2)}%` };
  };

  // Generate week markers
  const weekMarkers = [];
  const d = new Date(minDate);
  while (d <= maxDate) {
    const pos = ((d - minDate) / (1000 * 60 * 60 * 24) / totalDays) * 100;
    weekMarkers.push({ date: new Date(d), pos });
    d.setDate(d.getDate() + 7);
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="gantt-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="page-title">Planificación Gantt</h1>
          <p className="page-subtitle">Vista de tareas con plazos y responsables</p>
        </div>
        <Button className="btn-primary gap-2" onClick={() => {
          setEditingTask(null);
          setForm({ title: '', assigned_to: '', start_date: '', end_date: '', progress: 0, color: COLORS[0], category: 'Operaciones' });
          setShowDialog(true);
        }} data-testid="btn-new-gantt-task">
          <Plus size={18} /> Nueva Tarea
        </Button>
      </div>

      {tasks.length === 0 ? (
        <div className="card text-center py-12">
          <CalendarRange className="mx-auto h-12 w-12 text-[#E8E2D7] mb-4" />
          <p className="text-[#5C7083]">No hay tareas de planificación. Crea la primera.</p>
        </div>
      ) : (
        <div className="card p-0 overflow-hidden" data-testid="gantt-chart">
          <div className="overflow-x-auto">
            <div className="min-w-[800px]">
              {/* Timeline Header */}
              <div className="relative h-8 bg-[#F5F0E8] border-b border-[#E8E2D7]">
                {weekMarkers.map((wm, i) => (
                  <div key={`wm-header-${wm.pos}`} className="absolute top-0 h-full border-l border-[#E8E2D7] text-[10px] text-[#5C7083] px-1 pt-1" style={{ left: `${wm.pos}%` }}>
                    {wm.date.toLocaleDateString('es-CL', { day: '2-digit', month: 'short' })}
                  </div>
                ))}
              </div>

              {/* Task Rows */}
              {tasks.map((task, idx) => (
                <div key={task.id} className="flex items-center border-b border-[#E8E2D7] hover:bg-[#F5F0E8]/50 transition-colors" data-testid={`gantt-row-${task.id}`}>
                  {/* Task info */}
                  <div className="w-[280px] flex-shrink-0 p-3 border-r border-[#E8E2D7]">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#1A2835] text-sm truncate">{task.title}</p>
                        <p className="text-xs text-[#5C7083]">{task.assigned_to || 'Sin asignar'}</p>
                      </div>
                      <div className="flex gap-1 flex-shrink-0 ml-2">
                        <Button variant="ghost" size="sm" className="btn-ghost p-1 h-6 w-6" onClick={() => handleEdit(task)}>
                          <Edit size={12} />
                        </Button>
                        <Button variant="ghost" size="sm" className="btn-ghost p-1 h-6 w-6 text-[#C25953]" onClick={() => handleDelete(task.id)}>
                          <Trash2 size={12} />
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Gantt Bar */}
                  <div className="flex-1 relative h-12">
                    {weekMarkers.map((wm, i) => (
                      <div key={`wm-bar-${wm.pos}`} className="absolute top-0 h-full border-l border-[#E8E2D7]/50" style={{ left: `${wm.pos}%` }} />
                    ))}
                    <div className="absolute top-2 h-8 rounded-md flex items-center px-2 text-xs text-white font-medium cursor-pointer transition-all hover:opacity-90"
                      style={{ ...getBarStyle(task), backgroundColor: task.color || COLORS[idx % COLORS.length] }}
                      title={`${task.start_date} - ${task.end_date} (${task.progress}%)`}
                    >
                      <div className="absolute inset-0 rounded-md bg-black/20" style={{ right: `${100 - (task.progress || 0)}%` }} />
                      <span className="relative z-10 truncate">{task.progress}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Task List View */}
      {tasks.length > 0 && (
        <div className="card p-0 overflow-hidden" data-testid="gantt-list">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Tarea</th>
                  <th>Responsable</th>
                  <th>Categoría</th>
                  <th>Inicio</th>
                  <th>Fin</th>
                  <th>Progreso</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map(task => (
                  <tr key={task.id}>
                    <td className="font-medium">{task.title}</td>
                    <td>{task.assigned_to || '-'}</td>
                    <td><span className="status-badge badge-default">{task.category || '-'}</span></td>
                    <td>{task.start_date}</td>
                    <td>{task.end_date}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <div className="w-20 bg-[#E8E2D7] rounded-full h-2">
                          <div className="h-2 rounded-full transition-all" style={{ width: `${task.progress}%`, backgroundColor: task.color || '#2B4257' }} />
                        </div>
                        <input type="number" min="0" max="100" value={task.progress} onChange={e => handleProgressUpdate(task, parseInt(e.target.value) || 0)}
                          className="w-14 text-sm border border-[#E8E2D7] rounded px-1 py-0.5 text-center" data-testid={`progress-input-${task.id}`} />
                        <span className="text-xs text-[#5C7083]">%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingTask ? 'Editar Tarea' : 'Nueva Tarea Gantt'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSave} className="space-y-4" data-testid="gantt-form">
            <div>
              <Label className="form-label">Título *</Label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="form-input" required data-testid="input-gantt-title" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="form-label">Responsable</Label>
                <Input value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })} className="form-input" placeholder="Nombre" data-testid="input-gantt-assignee" />
              </div>
              <div>
                <Label className="form-label">Categoría</Label>
                <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="form-input" data-testid="select-gantt-category">
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="form-label">Fecha Inicio *</Label>
                <Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} className="form-input" required data-testid="input-gantt-start" />
              </div>
              <div>
                <Label className="form-label">Fecha Fin *</Label>
                <Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} className="form-input" required data-testid="input-gantt-end" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="form-label">Progreso (%)</Label>
                <Input type="number" min="0" max="100" value={form.progress} onChange={e => setForm({ ...form, progress: parseInt(e.target.value) || 0 })} className="form-input" data-testid="input-gantt-progress" />
              </div>
              <div>
                <Label className="form-label">Color</Label>
                <div className="flex gap-1 flex-wrap mt-1">
                  {COLORS.map(c => (
                    <button key={c} type="button" className={`w-7 h-7 rounded-md border-2 transition-all ${form.color === c ? 'border-[#1A2835] scale-110' : 'border-transparent'}`}
                      style={{ backgroundColor: c }} onClick={() => setForm({ ...form, color: c })} />
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)} className="btn-secondary">Cancelar</Button>
              <Button type="submit" className="btn-primary" disabled={saving || !form.title || !form.start_date || !form.end_date} data-testid="btn-save-gantt">
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {editingTask ? 'Guardar' : 'Crear'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Gantt;
